/**
 * GettingStartedModal — a first-run help overlay that shows the ClearIntake
 * Getting Started guide (served as a same-origin static asset at
 * /getting-started.html, rendered in an iframe so its own styling is preserved).
 *
 * Shown automatically on staff app load until the user ticks "Don't show this
 * again" (persisted in localStorage — a non-PHI UI preference), and re-openable
 * any time from the Help button in the top bar.
 */
import React, { useState } from 'react';

// Cloudflare Pages drops the .html extension (a /getting-started.html request
// 308-redirects to /getting-started), so point the iframe at the canonical path.
const GUIDE_URL = '/getting-started';

interface Props {
  /** Called when the user closes it; `dontShowAgain` reflects the checkbox. */
  onClose: (dontShowAgain: boolean) => void;
}

export default function GettingStartedModal({ onClose }: Props): React.ReactElement {
  const [dontShow, setDontShow] = useState(false);
  const close = () => onClose(dontShow);

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Getting Started guide">
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>👋 Welcome to ClearIntake — Getting Started</span>
          <button style={styles.closeX} onClick={close} aria-label="Close guide">✕</button>
        </div>

        <iframe
          src={GUIDE_URL}
          title="ClearIntake Getting Started Guide"
          style={styles.frame}
        />

        <div style={styles.footer}>
          <label style={styles.dontShow}>
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              style={styles.checkbox}
            />
            Don&rsquo;t show this again
          </label>
          <button style={styles.primary} onClick={close}>Got it</button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  panel: {
    background: '#fff', borderRadius: 12, overflow: 'hidden',
    width: 'min(1000px, 100%)', height: 'min(88vh, 900px)',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
  },
  title: { fontSize: 15, fontWeight: 700, color: '#111827' },
  closeX: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 16, color: '#6b7280', lineHeight: 1, padding: 4,
  },
  frame: { flex: 1, width: '100%', border: 'none', background: '#f6f8fb' },
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderTop: '1px solid #e5e7eb', flexShrink: 0, background: '#fff',
  },
  dontShow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' },
  checkbox: { width: 15, height: 15, cursor: 'pointer' },
  primary: {
    background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
};
